import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  getPermanentTeethInRange,
  getPrimaryTeethInRange,
  PERMANENT_TEETH,
  PRIMARY_TEETH,
  ToothDentition,
} from "../../utils/toothSelectionConstants";
import {
  appendSelectionValues,
  toggleSelectionValue,
} from "../../utils/selectionValueUtils";
import {
  SelectionModalTabs,
  SelectionModalTab,
  SelectionModalSection,
  SelectionSectionHeader,
  SelectionSectionHeaderTitle,
  SelectionSectionHeaderOption,
  SelectionSectionHeaderSeparator,
  DentitionToggle,
  SelectionModalSectionBody,
  SelectionModalSectionBodySpaced,
  ToothQuadrantRow,
  ToothQuadrant,
  ToothQuadrantLabel,
  ToothButtonsRow,
  SelectionOptionButton,
  SelectionOptionTooltipTarget,
  CustomInputRow,
  CustomInput,
  CustomAddButton,
  RangeFieldsRow,
  RangeField,
  RangeFieldLabel,
  SelectionTextInput,
  FullWidthActionButton,
  RangeLimitError,
} from "../SelectionModal/SelectionModal.styled";

const DisabledTabTooltipTarget = styled("span")({
  flex: 1,
  display: "flex",
});

type ToothSelectionTab = "single" | "range";
type ToothQuadrantPosition =
  | "upperRight"
  | "upperLeft"
  | "lowerRight"
  | "lowerLeft";

interface ToothSelectionPanelProps {
  value: string[];
  onChange: (value: string[]) => void;
  maxValues?: number;
  allowSingle?: boolean;
  allowRange?: boolean;
}

const ToothSelectionPanel: React.FC<ToothSelectionPanelProps> = ({
  value,
  onChange,
  maxValues,
  allowSingle = true,
  allowRange = true,
}) => {
  const { t } = useTranslation();
  const initialTab: ToothSelectionTab =
    allowSingle || !allowRange ? "single" : "range";
  const [activeTab, setActiveTab] = useState<ToothSelectionTab>(initialTab);
  const [dentition, setDentition] = useState<ToothDentition>("permanent");
  const [customValue, setCustomValue] = useState("");
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");

  useEffect(() => {
    if (activeTab === "single" && !allowSingle && allowRange) {
      setActiveTab("range");
      return;
    }
    if (activeTab === "range" && !allowRange && allowSingle) {
      setActiveTab("single");
    }
  }, [activeTab, allowRange, allowSingle]);

  const cannotSelectReason = t("treatmentPlan.cannotSelectWithTheseCodes");
  const onlyOneToothReason = t("treatmentPlan.onlyOneToothAllowed");
  const isAtMaxSelection =
    maxValues !== undefined && value.length >= maxValues;

  const pendingRangeValues = useMemo(() => {
    if (!rangeFrom.trim() || !rangeTo.trim()) return [];
    return dentition === "permanent"
      ? getPermanentTeethInRange(rangeFrom, rangeTo)
      : getPrimaryTeethInRange(rangeFrom, rangeTo);
  }, [dentition, rangeFrom, rangeTo]);

  const rangeWouldExceedMax = useMemo(() => {
    if (maxValues === undefined || pendingRangeValues.length === 0) {
      return false;
    }
    const selected = new Set(value);
    const newTeethCount = pendingRangeValues.filter(
      (tooth) => !selected.has(tooth),
    ).length;
    return value.length + newTeethCount > maxValues;
  }, [maxValues, pendingRangeValues, value]);

  const isRangeAddDisabled =
    pendingRangeValues.length === 0 || rangeWouldExceedMax;

  const renderTab = (
    tab: ToothSelectionTab,
    label: string,
    allowed: boolean,
  ) => {
    const tabButton = (
      <SelectionModalTab
        type="button"
        active={activeTab === tab}
        disabled={!allowed}
        onClick={() => {
          if (!allowed) return;
          setActiveTab(tab);
        }}
      >
        {label}
      </SelectionModalTab>
    );

    if (allowed) {
      return tabButton;
    }

    return (
      <Tooltip title={cannotSelectReason} arrow placement="top">
        <DisabledTabTooltipTarget>{tabButton}</DisabledTabTooltipTarget>
      </Tooltip>
    );
  };

  const handleToggle = (tooth: string | number) => {
    onChange(toggleSelectionValue(value, String(tooth), undefined, maxValues));
  };

  const handleCustomAdd = () => {
    if (!customValue.trim()) return;
    onChange(
      appendSelectionValues(
        value,
        [customValue],
        (item) => item.toUpperCase(),
        maxValues,
      ),
    );
    setCustomValue("");
  };

  const handleRangeAdd = () => {
    if (isRangeAddDisabled) return;

    const next = appendSelectionValues(
      value,
      pendingRangeValues,
      undefined,
      maxValues,
    );

    onChange(next);
    setRangeFrom("");
    setRangeTo("");
  };

  const renderQuadrant = (
    label: string,
    teeth: readonly (string | number)[],
    position: ToothQuadrantPosition,
  ) => {
    const isLower = position === "lowerRight" || position === "lowerLeft";
    const align =
      position === "upperRight" || position === "lowerRight" ? "left" : "right";

    const buttons = (
      <ToothButtonsRow>
        {teeth.map((tooth) => {
          const toothValue = String(tooth);
          const isSelected = value.includes(toothValue);
          const isDisabled = isAtMaxSelection && !isSelected;
          const toothButton = (
            <SelectionOptionButton
              type="button"
              selected={isSelected}
              disabled={isDisabled}
              onClick={() => {
                if (isDisabled) return;
                handleToggle(tooth);
              }}
            >
              {toothValue}
            </SelectionOptionButton>
          );

          if (!isDisabled) {
            return (
              <SelectionOptionTooltipTarget key={toothValue}>
                {toothButton}
              </SelectionOptionTooltipTarget>
            );
          }

          return (
            <Tooltip
              key={toothValue}
              title={
                maxValues === 1
                  ? onlyOneToothReason
                  : t("treatmentPlan.maxTeethReached")
              }
              arrow
              placement="top"
            >
              <SelectionOptionTooltipTarget>
                {toothButton}
              </SelectionOptionTooltipTarget>
            </Tooltip>
          );
        })}
      </ToothButtonsRow>
    );

    const labelElement = (
      <ToothQuadrantLabel align={align}>{label}</ToothQuadrantLabel>
    );

    return (
      <ToothQuadrant quadrantPosition={position}>
        {isLower ? (
          <>
            {buttons}
            {labelElement}
          </>
        ) : (
          <>
            {labelElement}
            {buttons}
          </>
        )}
      </ToothQuadrant>
    );
  };

  const renderDentitionHeader = () => (
    <DentitionToggle>
      <SelectionSectionHeaderOption
        type="button"
        active={dentition === "permanent"}
        onClick={() => setDentition("permanent")}
      >
        {t("treatmentPlan.toothPermanent")}
      </SelectionSectionHeaderOption>
      <SelectionSectionHeaderSeparator>/</SelectionSectionHeaderSeparator>
      <SelectionSectionHeaderOption
        type="button"
        active={dentition === "primary"}
        onClick={() => setDentition("primary")}
      >
        {t("treatmentPlan.toothPrimary")}
      </SelectionSectionHeaderOption>
    </DentitionToggle>
  );

  return (
    <>
      <SelectionModalTabs>
        {renderTab("single", t("treatmentPlan.toothTabSingle"), allowSingle)}
        {renderTab("range", t("treatmentPlan.toothTabRange"), allowRange)}
      </SelectionModalTabs>

      {activeTab === "single" && allowSingle ? (
        <>
          {renderDentitionHeader()}

          <SelectionModalSectionBodySpaced>
            {dentition === "permanent" ? (
              <>
                <ToothQuadrantRow>
                  {renderQuadrant(
                    t("treatmentPlan.quadrantUpperRight"),
                    PERMANENT_TEETH.upperRight,
                    "upperRight",
                  )}
                  {renderQuadrant(
                    t("treatmentPlan.quadrantUpperLeft"),
                    PERMANENT_TEETH.upperLeft,
                    "upperLeft",
                  )}
                </ToothQuadrantRow>
                <ToothQuadrantRow>
                  {renderQuadrant(
                    t("treatmentPlan.quadrantLowerRight"),
                    PERMANENT_TEETH.lowerRight,
                    "lowerRight",
                  )}
                  {renderQuadrant(
                    t("treatmentPlan.quadrantLowerLeft"),
                    PERMANENT_TEETH.lowerLeft,
                    "lowerLeft",
                  )}
                </ToothQuadrantRow>
              </>
            ) : (
              <>
                <ToothQuadrantRow>
                  {renderQuadrant(
                    t("treatmentPlan.quadrantUpperRight"),
                    PRIMARY_TEETH.upperRight,
                    "upperRight",
                  )}
                  {renderQuadrant(
                    t("treatmentPlan.quadrantUpperLeft"),
                    PRIMARY_TEETH.upperLeft,
                    "upperLeft",
                  )}
                </ToothQuadrantRow>
                <ToothQuadrantRow>
                  {renderQuadrant(
                    t("treatmentPlan.quadrantLowerRight"),
                    PRIMARY_TEETH.lowerRight,
                    "lowerRight",
                  )}
                  {renderQuadrant(
                    t("treatmentPlan.quadrantLowerLeft"),
                    PRIMARY_TEETH.lowerLeft,
                    "lowerLeft",
                  )}
                </ToothQuadrantRow>
              </>
            )}
          </SelectionModalSectionBodySpaced>

          <SelectionModalSection>
            <SelectionSectionHeader>
              <SelectionSectionHeaderTitle>
                {t("treatmentPlan.customNumber")}
              </SelectionSectionHeaderTitle>
            </SelectionSectionHeader>
            <SelectionModalSectionBody>
              <CustomInputRow>
                <CustomInput
                  value={customValue}
                  placeholder={t("treatmentPlan.enterPlaceholder")}
                  disabled={isAtMaxSelection}
                  onChange={(event) => setCustomValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      if (isAtMaxSelection) return;
                      handleCustomAdd();
                    }
                  }}
                />
                <CustomAddButton
                  type="button"
                  disabled={isAtMaxSelection}
                  onClick={() => {
                    if (isAtMaxSelection) return;
                    handleCustomAdd();
                  }}
                >
                  {t("treatmentPlan.add")}
                </CustomAddButton>
              </CustomInputRow>
            </SelectionModalSectionBody>
          </SelectionModalSection>
        </>
      ) : allowRange ? (
        <SelectionModalSectionBody>
          <RangeFieldsRow>
            <RangeField>
              <RangeFieldLabel>{t("treatmentPlan.from")}</RangeFieldLabel>
              <SelectionTextInput
                value={rangeFrom}
                placeholder={t("treatmentPlan.enterPlaceholder")}
                onChange={(event) => setRangeFrom(event.target.value)}
              />
            </RangeField>
            <RangeField>
              <RangeFieldLabel>{t("treatmentPlan.to")}</RangeFieldLabel>
              <SelectionTextInput
                value={rangeTo}
                placeholder={t("treatmentPlan.enterPlaceholder")}
                onChange={(event) => setRangeTo(event.target.value)}
              />
            </RangeField>
          </RangeFieldsRow>

          <FullWidthActionButton
            type="button"
            disabled={isRangeAddDisabled}
            onClick={handleRangeAdd}
          >
            {t("treatmentPlan.addRange")}
          </FullWidthActionButton>
          {rangeWouldExceedMax ? (
            <RangeLimitError>
              {t("treatmentPlan.maxTeethReached")}
            </RangeLimitError>
          ) : null}
        </SelectionModalSectionBody>
      ) : null}
    </>
  );
};

export default ToothSelectionPanel;
